/*
 * VoxPontBetaJoinPage — landing page for a clicked VoxPont Beta invite link.
 *
 * Served at /voxpont-beta-join?code=<inviteCode>.
 *
 * FLOW:
 *   - Signed out: prompt to sign in, returning here afterward via a sanitized
 *     `next` param on /voxpont-auth.
 *   - Signed in: auto-claim the invite (idempotent for the same user) and show
 *     success / error states.
 *
 * Bespoke navy + gold brand surface matching VoxPontAuthPage — no HealthDesk
 * branding, no MUI theme. Firebase: compat SDK only (via services/voxpontBeta).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { claimVoxPontBetaInvite } from "services/voxpontBeta";
import { VOXPONT_LEGAL_EFFECTIVE_DATE } from "pages/voxpontLegal/voxpontLegalContent";

const POST_CLAIM_ROUTE = "/voxpont12";

// Documents a beta tester must agree to before access is activated. The order
// here is the order shown in the consent sentence and recorded server-side.
const CONSENT_DOCUMENTS = [
  { key: "terms", label: "Terms of Use", path: "/voxpont-terms" },
  { key: "privacy", label: "Privacy Policy", path: "/voxpont-privacy" },
  { key: "baa", label: "Business Associate Agreement", path: "/voxpont-baa" },
];

const STYLES = `
.vpbeta-root {
  --navy-deep: #0C1E36;
  --navy: #122B4D;
  --royal: #1B4584;
  --gold-brushed: #8c6a1a;
  --gold-antique: #b08a28;
  --gold-polished: #d4af37;
  --gold-champagne: #c8a23a;
  --gold-metal: linear-gradient(105deg,#7a5e17 0%,#8c6a1a 16%,#b08a28 34%,#d4af37 50%,#b08a28 66%,#8c6a1a 84%,#7a5e17 100%);
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--navy-deep);
  padding: 24px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}
.vpbeta-card {
  position: relative;
  width: 100%;
  max-width: 460px;
  background: var(--navy);
  border: 1px solid rgba(196,154,44,0.18);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
}
.vpbeta-goldbar {
  height: 3px;
  width: 100%;
  background: var(--gold-metal);
}
.vpbeta-body { padding: 40px 36px 36px; text-align: center; }
.vpbeta-wordmark {
  font-family: 'Federo', serif;
  font-size: 1.8rem;
  line-height: 1.1;
  margin: 0;
  background: var(--gold-metal);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--gold-polished);
}
.vpbeta-subline {
  margin: 8px 0 0;
  text-transform: uppercase;
  color: var(--gold-antique);
  font-size: 0.65rem;
  letter-spacing: 0.2em;
}
.vpbeta-headline {
  margin: 28px 0 8px;
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 700;
}
.vpbeta-text {
  margin: 0 0 8px;
  color: rgba(255,255,255,0.6);
  font-size: 0.95rem;
  line-height: 1.5;
}
.vpbeta-expiry {
  margin: 14px 0 0;
  color: var(--gold-champagne);
  font-size: 0.85rem;
}
.vpbeta-spinner {
  width: 36px;
  height: 36px;
  margin: 28px auto 16px;
  border: 3px solid rgba(255,255,255,0.15);
  border-top-color: var(--gold-polished);
  border-radius: 50%;
  animation: vpbeta-spin 0.9s linear infinite;
}
@keyframes vpbeta-spin { to { transform: rotate(360deg); } }
.vpbeta-error {
  background: rgba(139,32,32,0.18);
  border: 1px solid rgba(229,120,120,0.4);
  color: #f3c3c3;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.9rem;
  margin: 24px 0 0;
}
.vpbeta-btn {
  display: inline-block;
  width: 100%;
  margin-top: 24px;
  background: var(--gold-metal);
  color: var(--navy-deep);
  border: none;
  border-radius: 9px;
  padding: 14px 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
  box-sizing: border-box;
}
.vpbeta-btn:hover:not(:disabled) { filter: brightness(1.05); }
.vpbeta-btn:disabled { opacity: 0.6; cursor: default; }
.vpbeta-agree {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-top: 24px;
  text-align: left;
  cursor: pointer;
}
.vpbeta-checkbox {
  margin: 2px 0 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: var(--gold-polished);
  cursor: pointer;
}
.vpbeta-agree-text {
  color: rgba(255,255,255,0.72);
  font-size: 0.88rem;
  line-height: 1.55;
}
.vpbeta-doclink {
  color: var(--gold-polished);
  font-weight: 600;
  text-decoration: none;
}
.vpbeta-doclink:hover { text-decoration: underline; }
`;

function friendlyClaimError(err) {
  switch (err?.code) {
    case "not-found":
    case "functions/not-found":
      return "We couldn't find that invite. Please double-check your link.";
    case "permission-denied":
    case "functions/permission-denied":
      return "This invite has been revoked and is no longer valid.";
    case "already-exists":
    case "functions/already-exists":
      return "This invite has already been used by another account.";
    case "deadline-exceeded":
    case "functions/deadline-exceeded":
      return "This invite has expired.";
    case "invalid-argument":
    case "functions/invalid-argument":
      return "This invite link is missing its code.";
    case "failed-precondition":
    case "functions/failed-precondition":
      return "You must agree to the Terms of Use, Privacy Policy, and Business Associate Agreement to activate access.";
    default:
      return "Something went wrong activating your access. Please try again.";
  }
}

function formatExpiry(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

export default function VoxPontBetaJoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const { user, userLoading } = useAuth();

  // 'loading' | 'signedOut' | 'consent' | 'claiming' | 'success' | 'error'
  const [status, setStatus] = useState("loading");
  const [expiresAt, setExpiresAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [agreed, setAgreed] = useState(false);

  // Resolve the initial post-auth state. We deliberately do NOT auto-claim:
  // a signed-in user must explicitly agree to the governing documents first.
  // The functional update keeps consent/claiming/success/error sticky once a
  // user-driven flow has started, even if auth identity references change.
  useEffect(() => {
    if (userLoading) {
      setStatus("loading");
      return;
    }

    if (!user || user.isAnonymous) {
      setStatus("signedOut");
      return;
    }

    if (!code) {
      setStatus("error");
      setErrorMsg("This invite link is missing its code.");
      return;
    }

    setStatus((prev) =>
      prev === "loading" || prev === "signedOut" ? "consent" : prev
    );
  }, [user, userLoading, code]);

  const handleActivate = () => {
    if (!agreed || !code) return;
    setStatus("claiming");
    claimVoxPontBetaInvite(code, {
      agreed: true,
      documents: CONSENT_DOCUMENTS.map((d) => d.key),
      version: VOXPONT_LEGAL_EFFECTIVE_DATE,
    })
      .then((result) => {
        setExpiresAt(result?.expiresAt ?? null);
        setStatus("success");
      })
      .catch((err) => {
        setErrorMsg(friendlyClaimError(err));
        setStatus("error");
      });
  };

  const signInHref = `/voxpont-auth?next=${encodeURIComponent(
    `/voxpont-beta-join?code=${code ?? ""}`
  )}`;

  const expiryLabel = formatExpiry(expiresAt);

  return (
    <div className="vpbeta-root">
      <style>{STYLES}</style>
      <div className="vpbeta-card">
        <div className="vpbeta-goldbar" />
        <div className="vpbeta-body">
          <h1 className="vpbeta-wordmark">VoxPont</h1>
          <p className="vpbeta-subline">VOX PONT — Voice across languages</p>

          {(status === "loading" || status === "claiming") && (
            <>
              <div className="vpbeta-spinner" aria-label="Activating" />
              <p className="vpbeta-text">
                {status === "claiming"
                  ? "Activating your beta access…"
                  : "Loading your invitation…"}
              </p>
            </>
          )}

          {status === "signedOut" && (
            <>
              <h2 className="vpbeta-headline">You've been invited to VoxPont Beta</h2>
              <p className="vpbeta-text">
                Sign in to activate your access. You'll return here automatically.
              </p>
              <button
                type="button"
                className="vpbeta-btn"
                onClick={() => navigate(signInHref)}
              >
                Sign in to activate your access
              </button>
            </>
          )}

          {status === "consent" && (
            <>
              <h2 className="vpbeta-headline">You've been invited to VoxPont Beta</h2>
              <p className="vpbeta-text">
                Before activating your access, please review and agree to the
                documents that govern your use of VoxPont.
              </p>
              <label className="vpbeta-agree">
                <input
                  type="checkbox"
                  className="vpbeta-checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span className="vpbeta-agree-text">
                  I have read and agree to the{" "}
                  {CONSENT_DOCUMENTS.map((doc, i) => (
                    <React.Fragment key={doc.key}>
                      {i > 0 && (i === CONSENT_DOCUMENTS.length - 1 ? ", and " : ", ")}
                      <a
                        className="vpbeta-doclink"
                        href={doc.path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.label}
                      </a>
                    </React.Fragment>
                  ))}
                  .
                </span>
              </label>
              <button
                type="button"
                className="vpbeta-btn"
                disabled={!agreed}
                onClick={handleActivate}
              >
                Agree &amp; activate access
              </button>
            </>
          )}

          {status === "success" && (
            <>
              <h2 className="vpbeta-headline">You're in. Welcome to VoxPont Beta.</h2>
              <p className="vpbeta-text">Your beta access is active.</p>
              {expiryLabel ? (
                <p className="vpbeta-expiry">Access valid through {expiryLabel}</p>
              ) : null}
              <button
                type="button"
                className="vpbeta-btn"
                onClick={() => navigate(POST_CLAIM_ROUTE)}
              >
                Go to VoxPont
              </button>
            </>
          )}

          {status === "error" && (
            <div className="vpbeta-error" role="alert">
              {errorMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
